import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import { ApiError } from '../api/client'
import { shutdownDevice } from '../api/devices'
import {
  deviceIsolationQuery,
  isolateDevice,
  ransomwareSuspectsQuery,
  releaseDevice,
  stopSuspectProcess,
  type DeviceIsolation,
  type RansomwareSuspect,
  type IsolationLevel,
} from '../api/securitee'
import { hrefOf } from '../overview/href'
import { Modal } from '../ui/Modal'
import { toast } from '../ui/notify'

/**
 * Raspunsul la ransomware, sub setarile din fila Antiransomware.
 *
 * HOTARAREA (Roxana, 28 septembrie 2026): agentul doar DETECTEAZA si ALARMEAZA
 * (fisiere-capcana, extensiile firmei). Izolarea, eliberarea si oprirea le face
 * NUMAI omul, de aici, cu confirmare. Nicio blocare automata pe aparat.
 *
 *  - Isolate  -> comanda ISO: Windows Firewall opreste tot traficul, mai putin
 *                legatura cu OptimumDesk, ca Release sa mai ajunga;
 *  - Release  -> comanda RLS: regulile de firewall de dinainte, exact;
 *  - Shut down -> `remote-ops/shutdown` fortat, acelasi capat ca fila Remote
 *                Operations (nu o comanda noua);
 *  - Running applications -> fisa aparatului din Proactivitee, fila Active
 *                Applications, unde fiecare proces are butonul Close (KPR).
 *                Acelasi loc spre care duce linkul din tichetul si mailul
 *                "[Ransomware ALERT]".
 *
 *  - Suspected processes (Microsoft Defender) -> programele pe care Controlled
 *                Folder Access le-a oprit (1123) sau le-ar fi oprit (1124) pe
 *                aparat; agentul nu se uita singur la procese. Stop process
 *                (KPR cu PID + calea de la Defender) numai cand programul e in
 *                ultimul raport de aplicatii active, cu confirmare.
 *
 * Fara dreptul de citire (403) sau fara capat (404) panoul nu se deseneaza.
 * Cat timp o comanda e pe drum, starea se reciteste la 5 secunde.
 */

export const ISOLATE_TEXT =
  'Windows Firewall on the device blocks all network traffic except the connection to OptimumDesk, so you can release it from here. Survives restarts.'
export const ISOLATE_INTERNAL_TEXT =
  'Windows Firewall on the device blocks the other machines on the company network (private addresses: servers, shares, other workstations). The internet and OptimumDesk stay open. Use it when something spreads inside the network.'
export const ISOLATE_EXTERNAL_TEXT =
  'Windows Firewall on the device blocks the internet. The company network (domain controller, shares, printers) and OptimumDesk stay open. Use it against data theft or malware talking to its server.'
export const LEVEL_NOTE = 'Older agents ignore the level and isolate the device completely.'
export const RELEASE_TEXT = 'Restores the firewall rules the device had before isolation.'
export const SHUTDOWN_TEXT = 'Forces the device to shut down now. Unsaved work on it is lost.'
export const DETECTION_TEXT =
  'Bait files and ransomware extensions only raise an alert (email and ticket). Nothing is blocked automatically: isolation and shutdown happen only from these buttons.'

export const SUSPECTS_TITLE = 'Suspected processes (Microsoft Defender)'
export const CFA_OFF_TEXT =
  'Controlled Folder Access is Off on this device, so Microsoft Defender does not report which programs write to protected folders. Set it to Audit or Block to see suspects here.'
export const NOT_RUNNING_TEXT = 'Not running'

export function stopText(path: string, pid: number): string {
  return `Stop ${path} (PID ${pid}) on this device? The agent stops it only if PID ${pid} is still this program, and never stops critical Windows processes.`
}

const ACTION_LABEL: Record<RansomwareSuspect['actions'][number], string> = {
  blocked: 'Blocked',
  audited: 'Audited (would be blocked)',
}

/** Fila Active Applications a fisei, prin aceeasi regula ca celelalte linkuri catre file de fisa (hrefOf). */
export function runningAppsHref(id: number): string {
  return hrefOf(`proactivitee/runningApplications/${id}`) ?? `/app/proactivitee/device/${id}#open=runningApplications`
}

type Action = 'isolate' | 'isolate_internal' | 'isolate_external' | 'release' | 'shutdown'

const CONFIRM: Record<Action, { title: string; text: string; button: string; className: string }> = {
  isolate: { title: 'Isolate device', text: ISOLATE_TEXT, button: 'Isolate', className: 'btn btn-danger' },
  isolate_internal: { title: 'Isolate from the company network', text: `${ISOLATE_INTERNAL_TEXT} ${LEVEL_NOTE}`, button: 'Isolate from LAN', className: 'btn btn-danger' },
  isolate_external: { title: 'Isolate from the internet', text: `${ISOLATE_EXTERNAL_TEXT} ${LEVEL_NOTE}`, button: 'Isolate from internet', className: 'btn btn-danger' },
  release: { title: 'Release isolation', text: RELEASE_TEXT, button: 'Release', className: 'btn btn-primary' },
  shutdown: { title: 'Shut down device', text: SHUTDOWN_TEXT, button: 'Shut down', className: 'btn btn-danger' },
}

const LEVEL_TEXT: Record<string, string> = {
  internal: ' from the company network (internet open)',
  external: ' from the internet (company network open)',
}

function describe(d: DeviceIsolation): string {
  const since = d.since ? ` since ${d.since}` : ''
  const level = LEVEL_TEXT[d.level ?? ''] ?? ''
  switch (d.state) {
    case 'isolated':
      return `Isolated${level}${since}.`
    case 'isolating':
      return `Isolation${level} pending${since} - it applies when the device next contacts the server.`
    case 'releasing':
      return `Release pending${since} - it applies when the device next contacts the server.`
    default:
      return 'Not isolated.'
  }
}

function pending(d: DeviceIsolation | undefined): boolean {
  return d?.state === 'isolating' || d?.state === 'releasing'
}

/**
 * Folosit si de butonul Respond din Securitee > Incidents (`IncidentsPage`),
 * pe aparatul incidentului: alt titlu, fara lista de suspecti Defender.
 */
export function RansomwareResponse({
  id,
  title = 'Ransomware response',
  suspects = true,
  levels = false,
  onAction,
  layout = 'inline',
  recommended = null,
  between,
}: {
  id: number
  title?: string
  suspects?: boolean
  /** Respond: izolarea pe niveluri (totala / fara LAN / fara internet). */
  levels?: boolean
  /** `groups`: cardul Respond din Incidents - Contain, apoi `between` (sesiunile), apoi Device. */
  layout?: 'inline' | 'groups'
  /** Actiunea recomandata a incidentului: butonul ei primeste `respond-recommended`. */
  recommended?: string | null
  between?: ReactNode
  /** Dupa orice comanda trimisa (Respond: se reciteste jurnalul de actiuni al incidentului). */
  onAction?: () => void
}) {
  const client = useQueryClient()
  const q = useQuery({
    ...deviceIsolationQuery(id),
    refetchInterval: (query) => (pending(query.state.data) ? 5000 : false),
  })
  const [confirm, setConfirm] = useState<Action | null>(null)
  const [busy, setBusy] = useState(false)

  if (q.error) {
    if (q.error instanceof ApiError && (q.error.status === 403 || q.error.status === 404)) return null
    return <div className="alert alert-danger">Something went wrong: {q.error.message}</div>
  }
  if (!q.data) return null
  const d = q.data

  const run = async (action: Action) => {
    if (busy) return
    setBusy(true)
    try {
      if (action === 'isolate' || action === 'isolate_internal' || action === 'isolate_external') {
        await isolateDevice(id, action === 'isolate' ? 'full' : action === 'isolate_internal' ? 'internal' : 'external')
        toast('Isolate instruction sent.', 'Success', 'success')
      } else if (action === 'release') {
        await releaseDevice(id)
        toast('Release instruction sent.', 'Success', 'success')
      } else {
        await shutdownDevice(id, '', '0', true)
        toast('Shut down instruction sent.', 'Success', 'success')
      }
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Something went wrong.', 'Error', 'error')
    } finally {
      setBusy(false)
      setConfirm(null)
      void client.invalidateQueries({ queryKey: deviceIsolationQuery(id).queryKey })
      onAction?.()
    }
  }

  const isolateDisabled = busy || d.state === 'isolated' || d.state === 'isolating'
  // cu niveluri: izolat la un nivel, celelalte doua il schimba; pe drum, nimic
  const levelDisabled = (level: IsolationLevel) =>
    busy || d.state === 'isolating' || d.state === 'releasing' || (d.state === 'isolated' && (d.level ?? 'full') === level)
  const releaseDisabled = busy || !(d.state === 'isolated' || d.state === 'isolating')
  const c = confirm ? CONFIRM[confirm] : null

  const mark = (action: string) => (recommended === action ? ' respond-recommended' : '')
  const contain = (
    <>
      <button type="button" className={'btn btn-danger securitee-isolate' + mark('isolate')} disabled={levels ? levelDisabled('full') : isolateDisabled} onClick={() => setConfirm('isolate')}>
        <i className="fa fa-shield-alt" aria-hidden="true"></i> Isolate
      </button>
      {levels ? (
        <>
          <button type="button" className={'btn btn-danger securitee-isolate-internal' + mark('isolate_internal')} disabled={levelDisabled('internal')} onClick={() => setConfirm('isolate_internal')}>
            <i className="fa fa-sitemap" aria-hidden="true"></i> Isolate from LAN
          </button>
          <button type="button" className={'btn btn-danger securitee-isolate-external' + mark('isolate_external')} disabled={levelDisabled('external')} onClick={() => setConfirm('isolate_external')}>
            <i className="fa fa-globe" aria-hidden="true"></i> Isolate from internet
          </button>
        </>
      ) : null}
      <button type="button" className="btn btn-primary securitee-release" disabled={releaseDisabled} onClick={() => setConfirm('release')}>
        <i className="fa fa-unlock" aria-hidden="true"></i> Release
      </button>
    </>
  )
  const device = (
    <>
      {d.can_shutdown ? (
        <button type="button" className={'btn btn-default securitee-shutdown' + mark('shutdown')} disabled={busy} onClick={() => setConfirm('shutdown')}>
          <i className="fa fa-power-off" aria-hidden="true"></i> Shut down
        </button>
      ) : null}
      <a className="btn btn-default securitee-running-apps" href={runningAppsHref(id)}>
        <i className="fa fa-tasks" aria-hidden="true"></i> Running applications
      </a>
    </>
  )

  return (
    <div className="securitee-ransomware-response" style={{ marginTop: 20 }}>
      {title !== '' ? <h4 className="bordered">{title}</h4> : null}
      <p className={`securitee-isolation-state securitee-isolation-${d.state}`} style={{ fontWeight: 600 }}>
        {d.state === 'isolated' ? <i className="fa fa-lock" aria-hidden="true"></i> : null} {describe(d)}
      </p>
      {d.last_error ? (
        <div className="alert alert-warning securitee-isolation-error">
          Last {d.last_error.command === 'ISO' ? 'isolate' : 'release'} failed: {d.last_error.message}
        </div>
      ) : null}
      <div className="securitee-usb-limits" style={{ fontSize: 11, color: '#7f8c8d', fontWeight: 400, marginBottom: 8 }}>
        {DETECTION_TEXT}
      </div>
      {layout === 'groups' ? (
        <>
          {d.can_isolate ? (
            <div className="xdr-group">
              <div className="xdr-caption">Contain</div>
              <div className="xdr-actions-grid">{contain}</div>
            </div>
          ) : null}
          {between}
          <div className="xdr-group">
            <div className="xdr-caption">Device</div>
            <div className="xdr-actions-row">{device}</div>
          </div>
        </>
      ) : (
        <div className="btn-group-inline" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {d.can_isolate ? contain : null}
          {device}
        </div>
      )}
      {suspects ? <SuspectedProcesses id={id} /> : null}
      <Modal
        id="securiteeRansomwareConfirm"
        open={confirm !== null}
        onClose={() => setConfirm(null)}
        title={c?.title ?? ''}
        portal
        footer={
          <>
            <button type="button" className="btn btn-default" onClick={() => setConfirm(null)}>
              Cancel
            </button>
            <button type="button" className={`${c?.className ?? 'btn btn-primary'} securitee-confirm`} disabled={busy} onClick={() => confirm && void run(confirm)}>
              {c?.button ?? 'OK'}
            </button>
          </>
        }
      >
        <p>{c?.text}</p>
      </Modal>
    </div>
  )
}

/**
 * Lista proceselor suspecte, din Microsoft Defender (Controlled Folder Access).
 * Butonul Stop process apare numai pentru un program care ruleaza in ultimul
 * raport de aplicatii active, si numai cu dreptul de scriere; cere confirmare.
 * Fara drept (403) sau fara aparat (404), sectiunea nu se deseneaza.
 */
export function SuspectedProcesses({ id }: { id: number }) {
  const client = useQueryClient()
  const q = useQuery(ransomwareSuspectsQuery(id))
  const [target, setTarget] = useState<{ path: string; pid: number } | null>(null)
  const [busy, setBusy] = useState(false)

  if (q.error) {
    if (q.error instanceof ApiError && (q.error.status === 403 || q.error.status === 404)) return null
    return <div className="alert alert-danger">Something went wrong: {q.error.message}</div>
  }
  if (!q.data) return null
  const d = q.data

  const stop = async () => {
    if (!target || busy) return
    setBusy(true)
    try {
      await stopSuspectProcess(id, target.path, target.pid)
      toast('Stop process instruction sent.', 'Success', 'success')
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Something went wrong.', 'Error', 'error')
    } finally {
      setBusy(false)
      setTarget(null)
      void client.invalidateQueries({ queryKey: ransomwareSuspectsQuery(id).queryKey })
    }
  }

  return (
    <div className="securitee-suspects" style={{ marginTop: 16 }}>
      <h5 style={{ fontWeight: 600 }}>{SUSPECTS_TITLE}</h5>
      {d.cfa_mode === 0 ? <div className="alert alert-info securitee-suspects-cfa-off">{CFA_OFF_TEXT}</div> : null}
      {d.suspects.length === 0 ? (
        <p className="securitee-suspects-empty">No process reported by Microsoft Defender since {d.since}.</p>
      ) : (
        <table className="table table-hover securitee-suspects-table">
          <thead>
            <tr>
              <th>Program</th>
              <th>User</th>
              <th>Count</th>
              <th>Last seen</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {d.suspects.map((s) => (
              <tr key={s.path}>
                <td style={{ wordBreak: 'break-all' }}>
                  {s.path}
                  <div style={{ fontSize: 11, color: '#7f8c8d' }}>{s.actions.map((a) => ACTION_LABEL[a]).join(', ')}</div>
                </td>
                <td>{s.user || 'unknown'}</td>
                <td>{s.count}</td>
                <td>{s.last_seen}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {s.running.length === 0 ? (
                    <span className="securitee-suspect-not-running" style={{ color: '#7f8c8d' }}>
                      {NOT_RUNNING_TEXT}
                    </span>
                  ) : (
                    s.running.map((r) =>
                      d.can_stop ? (
                        <button
                          key={r.pid}
                          type="button"
                          className="btn btn-danger btn-xs securitee-suspect-stop"
                          disabled={busy}
                          onClick={() => setTarget({ path: s.path, pid: r.pid })}
                        >
                          <i className="fa fa-stop" aria-hidden="true"></i> Stop process{s.running.length > 1 ? ` (PID ${r.pid})` : ''}
                        </button>
                      ) : (
                        <span key={r.pid} className="securitee-suspect-running">
                          Running (PID {r.pid})
                        </span>
                      ),
                    )
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div style={{ fontSize: 11, color: '#7f8c8d', fontWeight: 400 }}>
        {d.report_at ? `Running status from the running applications report of ${d.report_at}.` : 'No running applications report from this device yet.'}
      </div>
      <Modal
        id="securiteeSuspectStopConfirm"
        open={target !== null}
        onClose={() => setTarget(null)}
        title="Stop process"
        portal
        footer={
          <>
            <button type="button" className="btn btn-default" onClick={() => setTarget(null)}>
              Cancel
            </button>
            <button type="button" className="btn btn-danger securitee-suspect-confirm" disabled={busy} onClick={() => void stop()}>
              Stop process
            </button>
          </>
        }
      >
        <p>{target ? stopText(target.path, target.pid) : ''}</p>
      </Modal>
    </div>
  )
}
